import React from 'react';

import {
  Alert,
  Paper,
  Stack,
  Table,
  Text,
  Title
} from '@mantine/core';

import { useGetJSON } from '../utils/request';

interface RecurringJob {
  id: number;
  type: string;
  accountId: number;
  resourceType: string | null;
  resourceId: number | null;
  jobId: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  config: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface RecurringJobsResponse {
  success: boolean;
  recurrings: RecurringJob[];
}

const RECURRING_TYPE_LABELS: Record<string, string> = {
  'care_manager_digest': 'Care Manager Digest',
  'checkin_weekly_digest': 'Weekly Check-in Digest',
};

function getRecurringTypeLabel(type: string): string {
  return RECURRING_TYPE_LABELS[type] ?? type;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return '—';
  }
}

const RecurringJobs: React.FC = () => {
  const { data, error, loading } = useGetJSON<RecurringJobsResponse>(
    '/api/account/recurring_jobs'
  );

  if (loading) {
    return (
      <Stack gap="xl">
        <Title order={1}>Recurring Jobs</Title>
        <Text c="dimmed">Loading…</Text>
      </Stack>
    );
  }

  if (error) {
    const isForbidden = error.message.includes('403') || error.message.includes('Forbidden');
    return (
      <Stack gap="xl">
        <Title order={1}>Recurring Jobs</Title>
        <Alert color="red" title={isForbidden ? 'Access denied' : 'Error'}>
          {isForbidden
            ? 'This page is only available to account administrators.'
            : error.message}
        </Alert>
      </Stack>
    );
  }

  const jobs = data?.recurrings ?? [];

  return (
    <Stack gap="xl">
      <div>
        <Title order={1}>Recurring Jobs</Title>
        <Text c="dimmed" mt="xs" size="sm">
          Scheduled jobs for this account
        </Text>
      </div>

      {jobs.length === 0 ? (
        <Paper withBorder p="lg">
          <Text c="dimmed">No recurring jobs configured.</Text>
        </Paper>
      ) : (
        <Paper withBorder style={{ overflow: 'hidden' }}>
          <Table highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Job ID</Table.Th>
                <Table.Th>Next Run</Table.Th>
                <Table.Th>Last Run</Table.Th>
                <Table.Th>Resource</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {jobs.map((job) => (
                <Table.Tr key={job.id}>
                  <Table.Td>
                    <Text fw={500}>{getRecurringTypeLabel(job.type)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" style={{ fontFamily: 'monospace' }}>
                      {job.jobId ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{formatDate(job.nextRunAt)}</Table.Td>
                  <Table.Td>{formatDate(job.lastRunAt)}</Table.Td>
                  <Table.Td>
                    {job.resourceType && job.resourceId != null
                      ? `${job.resourceType.charAt(0).toUpperCase()}${job.resourceType.slice(1)} #${job.resourceId}`
                      : '—'}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
};

export default RecurringJobs;
